import type { UserRole } from '../types/crm';
import { canViewAllRecords, canViewAssignedTeam } from './roleAccess';

/**
 * Departments that may browse every account even with allowlist role = user.
 * Sales & Marketing is intentionally excluded — they only see assigned salesperson codes.
 */
export const ACCOUNT_VIEW_ALL_DEPARTMENT_SLUGS = [
  'management',
  'it',
  'admin',
  'customer_services',
  'finance',
] as const;

export type AccountViewAllDepartmentSlug = (typeof ACCOUNT_VIEW_ALL_DEPARTMENT_SLUGS)[number];

export function normalizeDepartmentSlug(slug: string | null | undefined): string {
  return (slug ?? '').trim().toLowerCase();
}

export function canViewAllAccountsByDepartment(slug: string | null | undefined): boolean {
  const value = normalizeDepartmentSlug(slug);
  return (ACCOUNT_VIEW_ALL_DEPARTMENT_SLUGS as readonly string[]).includes(value);
}

/** Role OR department grants full CRM browse/edit (accounts, contacts, projects, tasks). */
export function canViewAllAccounts(
  role: UserRole,
  departmentSlug?: string | null,
): boolean {
  return canViewAllRecords(role) || canViewAllAccountsByDepartment(departmentSlug);
}

/**
 * Who may assign project/task owner to another user:
 * supervisor, elevated roles, or finance / customer services / admin / it / management.
 */
export function canAssignProjectTaskOwner(
  role: UserRole,
  departmentSlug?: string | null,
): boolean {
  return canViewAssignedTeam(role) || canViewAllAccounts(role, departmentSlug);
}

/**
 * Who may pick CRM contacts on an account outside the actor's salesperson codes.
 * Admin / IT / Management (role or department) only — not CS/Finance.
 */
const CONTACT_ASSIGN_ELEVATED_DEPTS = ['admin', 'it', 'management'] as const;

export function canAssignContactOnForeignAccount(
  role: UserRole,
  departmentSlug?: string | null,
): boolean {
  if (role === 'admin' || role === 'it' || role === 'management') return true;
  const slug = normalizeDepartmentSlug(departmentSlug);
  return (CONTACT_ASSIGN_ELEVATED_DEPTS as readonly string[]).includes(slug);
}

/**
 * Whether the actor may browse/select contacts for the linked account on project/task forms.
 * Own salesperson-code accounts: yes. Foreign accounts: elevated only.
 */
export function canSelectContactsForLinkedAccount(
  role: UserRole,
  departmentSlug: string | null | undefined,
  accountIsOwnSalespersonCode: boolean,
): boolean {
  if (accountIsOwnSalespersonCode) return true;
  return canAssignContactOnForeignAccount(role, departmentSlug);
}
